/**
 * GLORIANA: the buy area of the BOLDI / hair perfume pages (snippets/gl-bundle.liquid), port of Bella Mia
 * assets/bm-pdp-bundle.js for the Shrine theme (Dawn cart drawer), 17.10.2026.
 *
 * - Cards: input[data-bundle="pack" | "single"]; the pack is preselected. The pack is N units (2 BOLDI / 3 perfume) of
 *   this product; the per-unit selects ([data-gl-slot-select]) choose each unit's shade / scent. BOLDI: slot 2 follows
 *   slot 1 until she changes slot 2 herself. Perfume: 3 different scents are preselected.
 * - Add (one POST /cart/add.js, Dawn sections cart-drawer + cart-icon-bubble): the units (same variant = one line with
 *   its quantity) + for the pack the gifts with the property _gl_gift "pack" (BOLDI: LED mirror, guide, mystery gift;
 *   perfume: 2 clip sets, guide, mystery gift), leaving out a gift the cart already holds + the toggled upsell row.
 *   selling_plan is sent from here only for the upsell row while the subscription box is checked (20.10.2026: the BOLDI
 *   brush joins the contract; the guard keeps a rider's plan only with a planned main line). Otherwise
 *   selling_plan is never sent from here: snippets/gl-plan-guard.liquid attaches the plan (box checked) to the units and
 *   lets the mystery gift ride with them; snippets/gl-gift-guard.liquid then keeps gifts, home delivery and insurance
 *   in line with the cart.
 * - The drawer opens through Shrine's <cart-drawer>.renderContents(response); without a drawer: /cart.
 * - Sticky bar: shown when the main button is off screen and the page is past the product gallery.
 * - The value line and the struck home-delivery price follow the subscription box.
 * - After a successful add: Shopify.analytics.publish('gl_atc', {pack, price, source, variant_id}) + DOM 'gl:atc'.
 */
(() => {
  if (customElements.get('gl-bundle')) return;
  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const ADDED_RESET_MS = 2500;

  class GlBundle extends HTMLElement {
    connectedCallback() {
      if (this.__init) return;
      this.__init = true;
      this.button = this.querySelector('[data-gl-submit]');
      this.buttonText = this.querySelector('[data-gl-submit-text]');
      this.errorEl = this.querySelector('[data-gl-error]');
      this.liveEl = this.querySelector('[data-gl-live]');
      this.sticky = this.querySelector('[data-gl-sticky]');
      this.stickyButton = this.querySelector('[data-gl-sticky-button]');
      this.stickyText = this.querySelector('[data-gl-sticky-text]');
      this.stickyWas = this.querySelector('[data-gl-sticky-was]');
      this.toPack = this.querySelector('[data-gl-to-pack]');
      this.upsells = this.querySelector('[data-gl-upsells]');
      this.slots = Array.from(this.querySelectorAll('[data-gl-slot]'));
      this.slot2Touched = false;
      this.busy = false;
      this.source = 'main';

      this.addEventListener('change', (e) => this.onChange(e));
      if (this.button) this.button.addEventListener('click', () => this.submit('main'));
      if (this.stickyButton) this.stickyButton.addEventListener('click', () => this.goToBundle());
      if (this.toPack) this.toPack.addEventListener('click', () => this.selectPack());
      this.sync();
      this.watchSticky();
    }

    get mode() { return this.dataset.mode || 'boldi'; }
    get packQty() { return Number(this.dataset.packQty) || 2; }
    get selected() {
      const c = this.querySelector('input[data-bundle]:checked');
      return c ? c.dataset.bundle : 'pack';
    }

    onChange(e) {
      const t = e.target;
      if (!(t instanceof HTMLElement)) return;
      if (t.matches('input[data-bundle]')) { this.sync(); this.hideError(); }
      if (t.matches('[data-gl-slot-select]')) {
        const slot = t.closest('[data-gl-slot]');
        const n = slot ? Number(slot.dataset.glSlot) : 0;
        if (this.mode === 'boldi') {
          if (n === 1 && !this.slot2Touched) {
            this.slots.slice(1).forEach((s) => { const sel = s.querySelector('select'); if (sel) sel.value = t.value; });
          }
          if (n > 1) this.slot2Touched = true;
        }
        this.hideError();
      }
      if (t.matches('input[data-gl-chk]')) {
        const row = t.closest('[data-gl-sub-row]');
        if (row) row.classList.toggle('is-on', t.checked);
        this.syncSub();
      }
    }

    subOn() {
      const chk = this.querySelector('input[data-gl-chk]');
      return !!(chk && chk.checked);
    }

    syncSub() {
      const on = this.subOn();
      this.querySelectorAll('[data-gl-value-v]').forEach((el) => { el.hidden = el.dataset.glValueV !== (on ? 'sub' : 'one'); });
      this.querySelectorAll('[data-gl-sub-only]').forEach((el) => { el.hidden = !on; });
    }

    sync() {
      const sel = this.selected;
      this.dataset.selected = sel;
      this.querySelectorAll('[data-option]').forEach((o) => o.classList.toggle('is-selected', o.dataset.option === sel));
      const qty = sel === 'pack' ? this.packQty : 1;
      this.slots.forEach((s, i) => { s.hidden = i >= qty; });
      const one = this.querySelector('[data-gl-pick-one]');
      const many = this.querySelector('[data-gl-pick-many]');
      if (one) one.hidden = qty > 1;
      if (many) many.hidden = qty === 1;
      const gifts = this.querySelector('[data-gl-pack-gifts]');
      if (gifts) gifts.setAttribute('aria-hidden', sel === 'pack' ? 'false' : 'true');
      if (this.toPack) this.toPack.hidden = sel === 'pack';
      this.syncSticky(sel);
      this.syncSub();
    }

    money(cents) {
      const whole = Math.floor(cents / 100);
      const frac = Math.round(cents % 100);
      const bdi = document.createElement('bdi');
      bdi.setAttribute('dir', 'rtl');
      bdi.textContent = `${whole}${frac > 0 ? '.' + String(frac).padStart(2, '0') : ''} ₪`;
      return bdi;
    }

    syncSticky(sel) {
      if (!this.stickyText) return;
      const pack = sel === 'pack';
      const label = (pack ? this.dataset.packLabel : this.dataset.singleLabel) || '';
      const cents = Number(pack ? this.dataset.packCents : this.dataset.singleCents) || 0;
      if (label && cents) this.stickyText.replaceChildren(document.createTextNode(`${label} · `), this.money(cents));
      if (this.stickyWas) {
        const was = Number(pack ? this.dataset.packCompareCents : this.dataset.singleCompareCents) || 0;
        const show = was > cents;
        if (show) this.stickyWas.replaceChildren(document.createTextNode(`${this.dataset.compareSr || ''} `), this.money(was));
        this.stickyWas.hidden = !show;
      }
    }

    selectPack() {
      const r = this.querySelector('input[data-bundle="pack"]');
      if (!r || r.disabled) return;
      r.checked = true;
      this.sync();
      this.hideError();
      const card = r.closest('.gl-qb__card');
      if (card && card.scrollIntoView) {
        const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        card.scrollIntoView({ block: 'nearest', behavior: reduce ? 'auto' : 'smooth' });
      }
    }

    unitIds() {
      const qty = this.selected === 'pack' ? this.packQty : 1;
      if (!this.slots.length) return Array.from({ length: qty }, () => Number(this.dataset.defaultId));
      return this.slots.slice(0, qty).map((s) => Number((s.querySelector('select') || {}).value)).filter(Boolean);
    }

    async cartNow() {
      try {
        const r = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin', cache: 'no-store' });
        return r.ok ? r.json() : null;
      } catch (e) { return null; }
    }

    async buildItems() {
      const items = [];
      const counts = new Map();
      this.unitIds().forEach((id) => counts.set(id, (counts.get(id) || 0) + 1));
      counts.forEach((quantity, id) => items.push({ id, quantity }));
      if (this.selected === 'pack') {
        const cart = await this.cartNow();
        const has = (id) => !!(cart && Array.isArray(cart.items) && cart.items.some((it) => it.variant_id === id));
        const gift = { _gl_gift: 'pack' };
        const g1 = Number(this.dataset.g1Id) || 0;
        const guide = Number(this.dataset.guideId) || 0;
        const myst = Number(this.dataset.mystId) || 0;
        if (g1 && !has(g1)) items.push({ id: g1, quantity: Number(this.dataset.g1Qty) || 1, properties: gift });
        if (guide && !has(guide)) items.push({ id: guide, quantity: 1, properties: gift });
        if (myst && !has(myst)) items.push({ id: myst, quantity: 1, properties: gift });
      }
      if (this.upsells) {
        this.upsells.querySelectorAll('[data-gl-upsell]').forEach((row) => {
          const t = row.querySelector('input[data-gl-upsell-toggle]');
          const id = Number(row.dataset.variantId) || 0;
          const plan = this.subOn() && window.__glPlan && window.__glPlan.plan;
          if (t && t.checked && id && !items.some((x) => x.id === id)) items.push(plan ? { id, quantity: 1, selling_plan: plan } : { id, quantity: 1 });
        });
      }
      return items;
    }

    async submit(source) {
      if (this.busy || !this.button || this.button.disabled) return;
      this.busy = true;
      this.button.setAttribute('aria-busy', 'true');
      this.hideError();
      const pack = this.selected === 'pack';
      try {
        const items = await this.buildItems();
        const res = await fetch(`${ROOT}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin',
          body: JSON.stringify({ items, sections: 'cart-drawer,cart-icon-bubble', sections_url: window.location.pathname }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) {
          this.showError(data.description || data.message || this.dataset.textError || '');
          return;
        }
        this.showAdded();
        this.publishAtc(source, pack, items[0] && items[0].id);
        this.openCart(data);
      } catch (err) {
        console.error('[gl-bundle]', err);
        this.showError(this.dataset.textError || '');
      } finally {
        this.busy = false;
        this.button.removeAttribute('aria-busy');
      }
    }

    openCart(data) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function' && data && data.sections) {
        drawer.classList.remove('is-empty');
        try { drawer.renderContents(data); return; } catch (e) { console.warn('[gl-bundle] drawer', e); }
      }
      window.location.href = `${ROOT}cart`;
    }

    publishAtc(source, pack, variantId) {
      const cents = Number(pack ? this.dataset.packCents : this.dataset.singleCents) || 0;
      const detail = { pack: pack ? 'pack' : 'single', price: cents / 100, source, variant_id: variantId || 0, mode: this.mode };
      try { document.dispatchEvent(new CustomEvent('gl:atc', { detail })); } catch (e) { /* never block the add */ }
      try { const a = window.Shopify && window.Shopify.analytics; if (a && typeof a.publish === 'function') a.publish('gl_atc', detail); } catch (e) { /* optional */ }
    }

    /* 21/10/2026 (Roni): the sticky bar no longer adds to the cart. It scrolls up so the bundle cards sit at the top
       and the main add-to-order button at the bottom of the screen, then that button pulses a few seconds and stops
       (.gl-atc-pop in snippets/gl-atc-pulse.liquid). When both do not fit on the screen, the button wins.
       Rollback: assets/gl-bundle-backup-20261021.js */
    goToBundle() {
      const btn = this.button;
      if (!btn) return;
      const title = this.querySelector('.gl-qb__title') || this.querySelector('.gl-qb') || btn;
      const header = document.querySelector('.section-header');
      const hp = header ? getComputedStyle(header).position : '';
      const headerH = header && (hp === 'sticky' || hp === 'fixed') ? header.offsetHeight : 0;
      const vh = window.innerHeight || document.documentElement.clientHeight;
      const y = window.pageYOffset || document.documentElement.scrollTop || 0;
      const fromTitle = title.getBoundingClientRect().top + y - headerH - 6;
      const forButton = btn.getBoundingClientRect().bottom + y - vh + 12;
      const smooth = !(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      if (fromTitle >= forButton) {
        title.style.scrollMarginTop = (headerH + 6) + 'px';
        title.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
      } else {
        btn.style.scrollMarginBottom = '12px';
        btn.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'end' });
      }
      clearTimeout(this.popTimer);
      clearTimeout(this.popEndTimer);
      btn.classList.remove('gl-atc-pop');
      this.popTimer = setTimeout(() => {
        void btn.offsetWidth;
        btn.classList.add('gl-atc-pop');
        this.popEndTimer = setTimeout(() => btn.classList.remove('gl-atc-pop'), 4600);
      }, smooth ? 650 : 0);
    }

    watchSticky() {
      if (!this.sticky || !this.button || !('IntersectionObserver' in window)) return;
      this.buttonInView = true;
      const gallery = document.querySelector('.product__media-wrapper') || document.querySelector('media-gallery');
      const past = () => !gallery || gallery.getBoundingClientRect().bottom < 0;
      const apply = () => {
        this.frame = 0;
        const show = !this.buttonInView && past();
        if (this.sticky.classList.contains('is-visible') === show) return;
        this.sticky.classList.toggle('is-visible', show);
        this.sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        if (this.stickyButton) this.stickyButton.tabIndex = show ? 0 : -1;
      };
      const schedule = () => { if (!this.frame) this.frame = requestAnimationFrame(apply); };
      new IntersectionObserver((entries) => {
        const e = entries[entries.length - 1];
        if (!e) return;
        this.buttonInView = e.isIntersecting;
        schedule();
      }, { threshold: 0 }).observe(this.button);
      document.addEventListener('scroll', schedule, { capture: true, passive: true });
      window.addEventListener('resize', schedule, { passive: true });
    }

    showAdded() {
      if (!this.button || !this.buttonText) return;
      clearTimeout(this.resetTimer);
      this.button.classList.add('is-added');
      this.buttonText.textContent = this.dataset.textAdded || '';
      if (this.liveEl) this.liveEl.textContent = this.dataset.textAdded || '';
      this.resetTimer = setTimeout(() => {
        this.button.classList.remove('is-added');
        this.buttonText.textContent = this.dataset.textAdd || '';
        if (this.liveEl) this.liveEl.textContent = '';
      }, ADDED_RESET_MS);
    }

    showError(msg) {
      if (!this.errorEl) return;
      this.errorEl.textContent = msg;
      this.errorEl.hidden = !msg;
    }

    hideError() { this.showError(''); }
  }

  customElements.define('gl-bundle', GlBundle);
})();
